/** /t — teacher home: courses, or the "try a demo course" empty state (UX flow 8). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { capabilities } from "../platform/capabilities";
import { createCourse, getHostInfo, getSyncStatus, listCourses, myReach, seedDemo } from "../teacher/api";
import { Button } from "../ui";
import { TeacherChrome } from "../teacher/Chrome";

export function Teacher() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const courses = useQuery({ queryKey: ["courses"], queryFn: listCourses });
  const sync = useQuery({ queryKey: ["sync-status"], queryFn: getSyncStatus, refetchInterval: 20_000, retry: false });
  const host = useQuery({ queryKey: ["host-info"], queryFn: getHostInfo, retry: false });
  const reach = useQuery({ queryKey: ["reach"], queryFn: myReach, retry: false });
  const isAdmin = !!reach.data && (reach.data.global_admin || reach.data.institutions.some((i) => i.role === "admin"));
  const demo = useMutation({
    mutationFn: () => seedDemo(i18n.language.startsWith("ru") ? "ru" : "en"),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ["courses"] });
      void navigate({ to: "/t/c/$courseId", params: { courseId: c.course_id } });
    },
  });

  const [title, setTitle] = useState("");
  const [lang, setLang] = useState(i18n.language.startsWith("ru") ? "ru" : "en");
  const create = useMutation({
    mutationFn: () => createCourse(title.trim(), lang),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ["courses"] });
      void navigate({ to: "/t/c/$courseId/plan", params: { courseId: c.course_id } });
    },
  });

  useEffect(() => {
    // On the desktop a 401 is the cloud behind the sidecar asking for a sign-in (code
    // cloud_signin); that happens in Settings, the local host session is fine.
    if (courses.error instanceof ApiError && courses.error.status === 401) void navigate({ to: capabilities.desktop ? "/t/settings" : "/signin" });
  }, [courses.error, navigate]);

  const newCourse = (
    <form
      className="flex flex-wrap items-end gap-3 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim()) create.mutate();
      }}
    >
      <label className="flex min-w-0 flex-1 flex-col gap-1 text-sm">
        {t("teacher.newTitle")}
        <input className="field" name="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("teacher.newTitleHint")} maxLength={200} autoComplete="off" required />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        {t("teacher.lang")}
        <select className="field" name="lang" value={lang} onChange={(e) => setLang(e.target.value)}>
          <option value="en">EN</option>
          <option value="ru">RU</option>
        </select>
      </label>
      <Button type="submit" loading={create.isPending} disabled={!title.trim()}>{t("teacher.newCourse")}</Button>
      {create.error && <p role="alert" className="w-full text-sm" style={{ color: "var(--danger)" }}>{t("teacher.createFailed")}</p>}
    </form>
  );

  return (
    <TeacherChrome title={t("teacher.courses")}>
      {courses.isPending && <p>{t("app.loading")}</p>}
      <p className="flex flex-wrap gap-4 text-sm">
        <Link to="/t/devices" className="underline">{t("devices.link")}</Link>
        {isAdmin && <Link to="/t/institutions" className="underline">{t("inst.link")}</Link>}
      </p>
      {host.data?.local && (
        <p className="flex flex-wrap items-center gap-2 rounded-full bg-[color:var(--surface-2)] px-3 py-1 text-sm">
          <span aria-hidden>◌</span>
          {t("host.chip")}{" "}
          <Link to="/t/host" className="underline">{t("host.open")}</Link>{" "}
          <Link to="/t/settings" className="underline">{t("settings.link")}</Link>
        </p>
      )}
      {sync.data && sync.data.pending_sessions > 0 && (
        <p className="flex flex-wrap items-center gap-2 rounded-full bg-[color:var(--surface-2)] px-3 py-1 text-sm" role="status">
          <span aria-hidden>⇧</span>
          {t("teacher.pendingUpload", { count: sync.data.pending_sessions, events: sync.data.pending_events })}
          {sync.data.last_error && <span className="text-xs text-[color:var(--ink-2)]">{t("teacher.pendingUploadRetry")}</span>}
        </p>
      )}
      {courses.isError && !(courses.error instanceof ApiError && courses.error.status === 401) && (
        <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
          <p role="alert" className="text-sm">{t("teacher.loadFailed")}</p>
          <div className="mt-3">
            <Button variant="secondary" loading={courses.isFetching} onClick={() => void courses.refetch()}>{t("teacher.retry")}</Button>
          </div>
        </section>
      )}
      {courses.data && newCourse}
      {courses.data && courses.data.length === 0 && (
        <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-6 shadow-sm">
          <h2 className="mb-2 text-lg font-semibold">{t("teacher.emptyTitle")}</h2>
          <p className="mb-4 text-[color:var(--ink-2)]">{t("teacher.emptyBody")}</p>
          <Button onClick={() => demo.mutate()} loading={demo.isPending}>{t("teacher.tryDemo")}</Button>
        </section>
      )}
      {courses.data && courses.data.length > 0 && (
        <ul className="flex flex-col gap-3">
          {courses.data.map((c) => (
            <li key={c.course_id}>
              <Link
                to="/t/c/$courseId"
                params={{ courseId: c.course_id }}
                className="block rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm hover:brightness-95"
              >
                <span className="font-semibold">{c.title}</span>
                <span className="block text-sm text-[color:var(--ink-2)]">{c.description}</span>
              </Link>
            </li>
          ))}
          <li>
            <Button variant="tertiary" onClick={() => demo.mutate()} loading={demo.isPending}>{t("teacher.tryDemo")}</Button>
          </li>
        </ul>
      )}
    </TeacherChrome>
  );
}
